import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { collection, query, updateDoc, doc, writeBatch, getDocs, limit, orderBy, startAfter, where } from 'firebase/firestore';
import db from '../firebase'; // Adjust the import according to your project structure
import * as cheerio from 'cheerio';
import { FeedbackContext } from './FeedbackContext';

const ArticlesContext = createContext();

export const useArticles = () => useContext(ArticlesContext);

export const ArticlesProvider = ({ children, user }) => {
    const [articles, setArticles] = useState([]);
    const [lastVisible, setLastVisible] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState('');
    const { showFeedback } = useContext(FeedbackContext); // Use FeedbackContext
    const [isSearching, setIsSearching] = useState(false);


    const fetchArticlesByIds = useCallback(async (ids) => {
        setIsSearching(true); // Set searching to true when fetching by IDs
        if (!ids.length) {
            console.log('No ids provided to fetch');
            return;
        }

        try {

            const articlesRef = collection(db, 'articles');
            const q = query(articlesRef, where('__name__', 'in', ids));
            const snapshot = await getDocs(q);

            const fetchedArticles = snapshot.docs.map(doc => {
                const data = doc.data();
                // Validate required fields
                if (!data ||
                    !data.title ||
                    !data.link ||
                    (data.article && Object.keys(data.article).length === 0)) {
                    console.warn(`Invalid or empty article data for ID ${doc.id}:`, data);
                    return null;
                }
                // Clean and validate date
                const pubDate = new Date(data.pubDate.seconds ? data.pubDate.seconds * 1000 : data.pubDate);

                // Add safe handling for description
                let cleanDescription = '';
                if (data.description) {
                    try {
                        const $ = cheerio.load(data.description);
                        cleanDescription = $.text();
                    } catch (err) {
                        console.warn(`Error parsing description for article ${doc.id}:`, err);
                        cleanDescription = data.description || '';
                    }
                }

                // Ensure article object is properly structured
                const articleData = data.article && typeof data.article === 'object'
                    ? data.article
                    : { title: '', source: '', url: '', description: '' };

                return {
                    id: doc.id,
                    title: String(data.title || ''),
                    description: String(data.description || ''),
                    link: String(data.link || ''),
                    TLDR: String(data.TLDR || ''),
                    article: articleData,
                    cleanDescription,
                    pubDate,
                    tags: Array.isArray(data.tags) ? data.tags : [],
                    score: Number(data.score || 0),
                    status: String(data.status || 'new'),
                    feedURL: String(data.feedURL || ''),
                    image: String(data.image || ''),
                    googleImage: data.googleImage || null
                };
            }).filter(article => article !== null && (!article.article || Object.keys(article.article).length > 0))
                .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));



            if (fetchedArticles.length === 0) {
                showFeedback('No matching articles found', 'info');
                return;
            }
            console.log(fetchedArticles);
            setArticles(fetchedArticles);
            return fetchedArticles;
        } catch (error) {
            console.error('Error fetching articles by IDs:', error);
            showFeedback('Error fetching articles', 'error');
            return [];
        }
    }, [showFeedback]);


    const fetchInitialArticles = useCallback(async () => {
        if (isSearching) {
            console.log('Already searching, skipping initial fetch');
            return;
        }
        try {
            if (!user) {
                console.log('User not available');
                //return; //database rules allowing non-authenticated users to read
            }

            const articlesRef = collection(db, 'articles');
            let q;
            if (searchTerm) {
                q = query(
                    articlesRef,
                    where('title', '>=', searchTerm),
                    where('title', '<=', searchTerm + '\uf8ff'),
                    orderBy('title'),
                    limit(25)
                );
            } else {
                q = query(articlesRef, orderBy('pubDate', 'desc'), limit(20));
            }
            const snapshot = await getDocs(q);
            const fetchedArticles = snapshot.docs.map(doc => {
                const data = doc.data();
                const pubDate = new Date(data.pubDate.seconds ? data.pubDate.seconds * 1000 : data.pubDate);
                const $ = cheerio.load(data.description);
                const cleanDescription = $.text();
                const tags = data.tags || []; // Ensure tags is always an array
                return { id: doc.id, ...data, pubDate, cleanDescription, tags };
            });
            setArticles(fetchedArticles);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            console.log('LastVisible: ' + snapshot.docs[snapshot.docs.length - 1]);
        } catch (error) {
            console.error('Error fetching initial articles:', error);
            showFeedback('Error fetching articles', 'error');
        }

        //fetchInitialArticles();
    }, [user]);

    useEffect(() => {
        if (!isSearching) {
            fetchInitialArticles();
        }
    }, [user, fetchInitialArticles, isSearching]);


    const fetchNextArticles = useCallback(async (batchSize = 25) => {
        // Early return if we're in search mode
        if (isSearching) {
            console.log('Skipping pagination during search');
            return [];
        }
        if (!lastVisible) {
            console.log('Error with lastVisible:', lastVisible);
            return;
        }
        try {
            const articlesRef = collection(db, 'articles');
            let q;
            if (searchTerm) {
                q = query(
                    articlesRef,
                    where('title', '>=', searchTerm),
                    where('title', '<=', searchTerm + '\uf8ff'),
                    orderBy('title'),
                    startAfter(lastVisible),
                    limit(batchSize)
                );
            } else {
                q = query(articlesRef, orderBy('pubDate', 'desc'), startAfter(lastVisible), limit(batchSize));
            }
            const snapshot = await getDocs(q);
            const newArticles = snapshot.docs.map(doc => { //Mapping new articles: The newArticles array is created from the fetched documents.
                const data = doc.data();
                const pubDate = new Date(data.pubDate.seconds ? data.pubDate.seconds * 1000 : data.pubDate);
                const $ = cheerio.load(data.description);
                const cleanDescription = $.text();
                const tags = doc.data().tags || [] // Ensure tags is always an array
                return { id: doc.id, ...data, pubDate, cleanDescription, tags };
            });
            //Filtering duplicates: Before updating the state, we filter out any articles that already exist in the current list using a Set to track existing article IDs.
            //Updating state: The state is updated with the filtered list of new articles, ensuring no duplicates are added.            
            setArticles(prevArticles => {
                const articleIds = new Set(prevArticles.map(article => article.id));
                const filteredNewArticles = newArticles.filter(article => !articleIds.has(article.id));
                return [...prevArticles, ...filteredNewArticles];
            });
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            return newArticles; // Return the new articles for optional handling
        } catch (error) {
            console.error('Error fetching next articles:', error);
            return [];
        }
    }, [isSearching, lastVisible]);

    const handleFavoriteArticle = async (articleId, currentFavoriteStatus) => {
        setArticles(prevArticles =>
            prevArticles.map(article =>
                article.id === articleId ? { ...article, favorite: !currentFavoriteStatus } : article
            ));
        try {
            await updateDoc(doc(db, 'articles', articleId), { favorite: !currentFavoriteStatus });


        } catch (error) {
            console.error('Error toggling favorite status:', error);
        }
    };

    const handleTagChange = async (article, newTags) => {
        const MAX_TAGS = 2; // Set your desired limit here

        if (newTags.length > MAX_TAGS) {
            console.warn(`Cannot select more than ${MAX_TAGS} tags.`);
            showFeedback(`Cannot select more than ${MAX_TAGS} tags.`, 'error');
            return;
        }
        const combinedTags = Array.from(new Set(newTags));

        setArticles(prevArticles =>
            prevArticles.map(thisarticle =>
                thisarticle.id === article.id ? { ...thisarticle, tags: combinedTags } : thisarticle
            )
        );
        try {
            //console.log('combinedTags:', combinedTags);
            await updateDoc(doc(db, 'articles', article.id), { tags: combinedTags });

            //setSelectedTags(combinedTags);
        } catch (error) {
            console.error('Error updating tags:', error);
        }
    };

    const handleRemoveDuplicates = async () => {
        try {
            const articlesRef = collection(db, 'articles');
            const snapshot = await getDocs(articlesRef);
            const urlMap = new Map();
            const batch = writeBatch(db);

            snapshot.forEach(doc => {
                const data = doc.data();
                const url = data.link;

                if (urlMap.has(url)) {
                    batch.delete(doc.ref);
                } else {
                    urlMap.set(url, doc.id);
                }
            });

            await batch.commit();
            console.log('Duplicate entries removed successfully.');
        } catch (error) {
            console.error('Error removing duplicate entries:', error);
        }
    };

    const handleSearch = () => {
        setSearchTerm(inputValue);
    };

    const handleReset = () => {
        setInputValue('');
        setSearchTerm('');
        setIsSearching(false);
        fetchInitialArticles();
    };

    return (
        <ArticlesContext.Provider value={{
            articles,
            setArticles,
            fetchInitialArticles,
            fetchNextArticles,
            handleFavoriteArticle,
            handleTagChange,
            handleRemoveDuplicates,
            lastVisible,
            searchTerm,
            setSearchTerm,
            handleSearch,
            inputValue,
            setInputValue,
            handleReset,
            fetchArticlesByIds,
            isSearching,
            setIsSearching
        }}>
            {children}
        </ArticlesContext.Provider>
    );
};